import { useEffect, useState, useRef } from 'react';
import { Copy, Loader2, Download, Upload, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, ttsApi } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { useQuotaUpdate } from '../hooks/useQuotaUpdate';

const VoiceClone = () => {
  const { t } = useTranslation('voiceClone');
  const { user } = useAuthStore();
  const { refreshQuota } = useQuotaUpdate();
  const noQuota = !user || user.remaining <= 0;
  const [mode, setMode] = useState<'controllable' | 'ultimate'>('controllable');
  const [text, setText] = useState('');
  const [controlInstruction, setControlInstruction] = useState('');
  const [promptText, setPromptText] = useState('');
  const [cfgValue, setCfgValue] = useState(2.0);
  const [inferenceSteps, setInferenceSteps] = useState(10);
  const [referenceAudio, setReferenceAudio] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading) {
      setElapsedSeconds(0);
      return;
    }

    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [loading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setReferenceAudio(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referenceAudio) {
      setError(t('form.referenceAudioPlaceholder'));
      return;
    }

    setError('');
    setLoading(true);
    setAudioUrl(null);

    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('reference_audio', referenceAudio);
      formData.append('cfg_value', cfgValue.toString());
      formData.append('inference_timesteps', inferenceSteps.toString());

      let response;
      if (mode === 'controllable') {
        formData.append('control_instruction', controlInstruction);
        response = await ttsApi.controllableClone(formData);
      } else {
        if (!promptText.trim()) {
          setError(t('ultimateNeedPromptText'));
          setLoading(false);
          return;
        }
        formData.append('prompt_text', promptText);
        response = await ttsApi.ultimateClone(formData);
      }

      const blob = new Blob([response.data], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      refreshQuota();
    } catch (err: any) {
      setError(await extractApiErrorMessage(err, t('error')));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `voice_clone_${Date.now()}.wav`;
      link.click();
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600 mt-1">
          {t('description')}
        </p>
      </div>

      {/* 模式切换 */}
      <div className="card mb-6">
        <div className="flex gap-4">
          <button
            onClick={() => setMode('controllable')}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              mode === 'controllable'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-semibold text-gray-900">{t('modes.controllable.title')}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('modes.controllable.desc')}
            </p>
          </button>
          <button
            onClick={() => setMode('ultimate')}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              mode === 'ultimate'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-semibold text-gray-900">{t('modes.ultimate.title')}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('modes.ultimate.desc')}
            </p>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 表单区域 */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="card space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {noQuota && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{t('error.quotaExceeded', { defaultValue: '今日配额已用完，请明天再试或升级 VIP' })}</span>
              </div>
            )}

            {loading && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                <p className="font-medium">{t('loading.title')}</p>
                <p className="text-sm mt-1">
                  {t('loading.waitTime', { seconds: elapsedSeconds })}
                </p>
              </div>
            )}

            {/* 文件上传 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.referenceAudioLabel')}
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-500 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">
                  {referenceAudio ? t('form.referenceAudioUploaded', { fileName: referenceAudio.name }) : t('form.referenceAudioPlaceholder')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('form.supportFormats')}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.textLabel')}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="input h-32 resize-none"
                placeholder={t('form.textPlaceholder')}
                required
                maxLength={5000}
              />
            </div>

            {mode === 'controllable' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.controlInstructionLabel')}
                </label>
                <textarea
                  value={controlInstruction}
                  onChange={(e) => setControlInstruction(e.target.value)}
                  className="input h-24 resize-none"
                  placeholder={t('form.controlInstructionPlaceholder')}
                  maxLength={500}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.promptTextLabel')}
                </label>
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  className="input h-24 resize-none"
                  placeholder={t('form.promptTextPlaceholder')}
                  required={mode === 'ultimate'}
                  maxLength={5000}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('form.promptTextHint')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.cfgLabel', { value: cfgValue })}
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={cfgValue}
                  onChange={(e) => setCfgValue(parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">{t('form.cfgHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.stepsLabel', { value: inferenceSteps })}
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={inferenceSteps}
                  onChange={(e) => setInferenceSteps(parseInt(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">{t('form.stepsHint')}</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || noQuota}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('form.loading')}
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  {mode === 'controllable' ? t('form.controllableSubmit') : t('form.ultimateSubmit')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* 结果区域 */}
        <div>
          {audioUrl && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">{t('result.title')}</h3>
              <audio controls className="w-full mb-4">
                <source src={audioUrl} type="audio/wav" />
                {t('result.browserNotSupport')}
              </audio>
              <button
                onClick={handleDownload}
                className="w-full btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t('result.download')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceClone;
